import { ReactNode } from "react"
import { Link as InertiaLink, router, usePage } from "@inertiajs/react"
import {
  Alert, AppBar, Box, Button, Container, ContainerProps, CssBaseline, Drawer, List, ListItem,
  ListItemButton, ListItemText, Stack, ThemeProvider, Toolbar, Typography, createTheme
} from "@mui/material"

const drawerWidth = 240

const theme = createTheme({
  palette: { mode: "light", primary: { main: "#1976d2" } },
  typography: { fontFamily: '"Inter", system-ui, -apple-system, sans-serif' }
})

type Props = {
  title?: string
  /** Content width. Defaults to "lg"; pass false to fill the available space (e.g. list pages). */
  maxWidth?: ContainerProps["maxWidth"]
  children: ReactNode
}

const navItems = [
  { label: "Topics", href: "/admin/topics" },
  { label: "Sentences", href: "/admin/sentences" },
  { label: "Words", href: "/admin/words" },
  { label: "Conversations", href: "/admin/conversations" },
  { label: "New Sentence (AI)", href: "/admin/ai/sentence/new" }
]

export default function AdminLayout({ title = "Admin", maxWidth = "lg", children }: Props) {
  const signOut = () => router.delete("/users/sign_out")
  const { flash } = usePage<{ flash?: { notice?: string | null; alert?: string | null } }>().props

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex" }}>
        <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }} data-testid="admin-title">
              {title}
            </Typography>
            <Button color="inherit" component={InertiaLink} href="/">Home</Button>
            <Button color="inherit" onClick={signOut} data-testid="sign-out">Sign out</Button>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box" }
          }}
        >
          <Toolbar />
          <Box sx={{ overflow: "auto" }}>
            <List>
              {navItems.map((item) => (
                <ListItem key={item.href} disablePadding>
                  <ListItemButton component={InertiaLink} href={item.href} data-testid={`nav-${item.href}`}>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Toolbar />
          <Container maxWidth={maxWidth} disableGutters={!maxWidth}>
            <Stack gap={2} sx={{ mb: flash?.notice || flash?.alert ? 2 : 0 }}>
              {flash?.notice && <Alert severity="success" data-testid="flash-notice">{flash.notice}</Alert>}
              {flash?.alert && <Alert severity="error" data-testid="flash-alert">{flash.alert}</Alert>}
            </Stack>
            {children}
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  )
}
